// ── ONE DOCUMENT INDEX: what a file a grower or AIP sent IS, before any row it fed ──
//
// Roadmap step B, first slice. Every original is stored ONCE in the farmer-documents
// bucket at
//
//     {farmer_id}/{crop_year}/{doc_type}/{yyyymmdd}_{sha8}_{original name}
//
// identified by the SHA-256 of its bytes, and indexed by ONE farmer_documents row.
// Appraisals already follow that shape (src/claims/appraisalStore.js, #450); this is
// the same rule with the document type as a parameter, so the two cannot drift
// (tools/check-document-index.js asserts appraisalStoragePath === documentStoragePath
// for doc type "appraisal").
//
// ⚠ THE FARMER ID IS SEGMENT 1, NOT SEGMENT 2. The bucket's read rule (sql/016,
// doc_path_farmer_id) lets any portal user granted a farm read an object whose SECOND
// segment is that farm. A Schedule of Insurance / APH database / PP worksheet loaded
// through the agent's Import screen is an agency working document; at segment 1 only
// admins and book-wide 578 holders reach it — exactly who reaches the farmer_documents
// ROW today (its RLS grants nobody else). Nothing is widened.
//
// Two gaps this closes:
//   1. The Import screen read a PDF with AI, saved the book rows and THREW THE PDF AWAY.
//   2. A 578 that arrived as a document (Dropbox folder sync, document hub upload) was
//      filed in farmer_documents only, which the 578 audit and the season boards never
//      read. Such a file is now handed to the 578 intake (api/box.js ingestStored578)
//      under the same rules the Box sync applies; stored578Decision is that rule.
//
// CommonJS, no browser APIs — api/box.js requires it and the checker loads it as-is.

const INTAKE = require("./fsa578Intake");

const SHA = /^[0-9a-f]{64}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// What the Import screen's extractor ESTABLISHED, mapped to an index doc type. Only the
// types EXTRACTION_PROMPT names are listed; anything else is "other" with the raw type
// kept in notes — never a guessed label. Schedules keep the hub's existing
// "schedule_of_insurance" so the library filter shows them beside the Dropbox ones.
const DOC_TYPE_FOR_EXTRACTION = {
  SOI_CROP: "schedule_of_insurance",
  SOI_DRP: "schedule_of_insurance",
  APH_DATABASE: "aph_database",
  PP_REPORT: "pp_report",
  BATCH_PP: "pp_batch",
  PRF: "prf_schedule",
  LRP: "lrp_schedule",
};
function docTypeForExtraction(documentType) {
  const k = String(documentType || "").trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(DOC_TYPE_FOR_EXTRACTION, k) ? DOC_TYPE_FOR_EXTRACTION[k] : "other";
}

function safeFileName(name, fallback) {
  return String(name || fallback || "document.pdf").replace(/[^a-zA-Z0-9._-]/g, "_");
}

// The canonical object key. A farm not yet in the book (a first SOI for a new grower)
// is stored under "unassigned" and the ROW is given the farmer once the save names it —
// "unassigned" is not a uuid, so doc_path_farmer_id resolves nothing for it either way.
function documentStoragePath(farmerId, cropYear, docType, sha256, fileName, when) {
  const d = when instanceof Date ? when : new Date();
  const ymd = d.toISOString().slice(0, 10).replace(/-/g, "");
  const fid = farmerId && UUID.test(String(farmerId)) ? String(farmerId) : "unassigned";
  const cy = Number.isInteger(Number(cropYear)) && Number(cropYear) > 1900 ? String(Number(cropYear)) : "undated";
  const type = String(docType || "other").replace(/[^a-z0-9_]/gi, "_").toLowerCase() || "other";
  const sha8 = SHA.test(String(sha256 || "")) ? String(sha256).slice(0, 8) : "";
  return `${fid}/${cy}/${type}/${ymd}_${sha8}_${safeFileName(fileName)}`;
}

// PostgREST filter for "this farm already holds these bytes". Null for a malformed or
// missing hash, so a bad hash can never become a filter that matches every row.
function sameDocumentFilter(farmerId, sha256) {
  if (!SHA.test(String(sha256 || ""))) return null;
  const who = farmerId && UUID.test(String(farmerId)) ? `farmer_id=eq.${farmerId}` : "farmer_id=is.null";
  return `${who}&content_sha256=eq.${sha256}`;
}
// Until sql/103 adds content_sha256 the hash cannot be queried. The canonical path
// carries the first 8 hex of the hash AND the file name, so the same file re-imported
// is found by its path under the same farm. Weaker than a full hash (stated), and it
// can only ever say "same" for an identical name + identical hash prefix.
function samePathFilter(farmerId, sha256, fileName) {
  if (!SHA.test(String(sha256 || ""))) return null;
  const fid = farmerId && UUID.test(String(farmerId)) ? String(farmerId) : "unassigned";
  return `storage_path=like.${fid}/*_${String(sha256).slice(0, 8)}_${safeFileName(fileName)}`;
}

// Does this error mean "an index column sql/103 adds is not there yet"?
const INDEX_COLUMNS = ["content_sha256", "parse_status", "superseded_by", "season"];
function isMissingIndexColumn(err) {
  const s = String((err && (err.message || err)) || "");
  if (!INDEX_COLUMNS.some((c) => s.includes(c))) return false;
  return /42703|PGRST204|does not exist|schema cache/i.test(s);
}
function withoutIndexColumns(row) {
  const out = { ...row };
  for (const c of INDEX_COLUMNS) delete out[c];
  return out;
}

// The ONE index row. Every key is always present (PostgREST refuses mixed key sets).
function indexRow({ farmerId, policyNumber, docType, cropYear, title, fileName, source, storagePath, sha256, parseStatus, notes, uploadedBy }) {
  return {
    farmer_id: farmerId && UUID.test(String(farmerId)) ? farmerId : null,
    policy_number: policyNumber || null,
    doc_type: docType || "other",
    crop_year: Number.isInteger(Number(cropYear)) && Number(cropYear) > 1900 ? Number(cropYear) : null,
    title: title || String(fileName || "").replace(/\.pdf$/i, "") || null,
    original_filename: fileName || null,
    source: source || "manual_upload",
    storage_path: storagePath || null,
    content_sha256: SHA.test(String(sha256 || "")) ? sha256 : null,
    parse_status: parseStatus || null,
    notes: notes || null,
    uploaded_by: uploadedBy || null,
  };
}

// The Import screen's extraction results, one entry per FILE. A file that produced
// several farms (a BATCH_PP worksheet) belongs to none of them: farmerKey null.
function importKey(farmer) {
  if (!farmer) return null;
  return `${String(farmer.policyNumber || "").trim().toUpperCase()}|${farmer.cropYear || ""}|${String(farmer.name || "").trim().toLowerCase()}`;
}
function originalsFromResults(results) {
  const byFile = new Map();
  for (const r of results || []) {
    if (!r || !r.file || !r.farmer) continue;
    const name = r.file.name;
    const e = byFile.get(name) || { file: r.file, documentType: r.docType || null, farmers: new Map() };
    const k = importKey(r.farmer);
    if (k && !e.farmers.has(k)) e.farmers.set(k, r.farmer);
    byFile.set(name, e);
  }
  return [...byFile.values()].map((e) => {
    const farmers = [...e.farmers.values()];
    const one = farmers.length === 1 ? farmers[0] : null;
    return {
      file: e.file,
      documentType: e.documentType,
      docType: docTypeForExtraction(e.documentType),
      farmer: one,                                   // null when the file names several farms
      farmerCount: farmers.length,
      policyNumber: one ? one.policyNumber || null : null,
      cropYear: one ? one.cropYear || null : (farmers[0] && farmers[0].cropYear) || null,
    };
  });
}

// Which saved farm an original belongs to, read from the save results (each carries the
// farmerId saveFarmerToSupabase returned). Same policy number AND crop year, or nothing.
function savedFarmerIdFor(original, saveResults) {
  if (!original || !original.farmer) return null;
  const pn = String(original.farmer.policyNumber || "").trim().toUpperCase();
  const cy = Number(original.farmer.cropYear) || null;
  const nm = String(original.farmer.name || "").trim().toLowerCase();
  const hits = (saveResults || []).filter((r) => r && r.ok && r.farmerId && UUID.test(String(r.farmerId))
    && (pn ? String(r.policyNumber || "").trim().toUpperCase() === pn && (Number(r.cropYear) || null) === cy
           : String(r.name || "").trim().toLowerCase() === nm));
  const ids = [...new Set(hits.map((r) => r.farmerId))];
  return ids.length === 1 ? ids[0] : null;          // two different farms → claim neither
}

// What the app shows as imported. ONLY farms whose save succeeded — a farm whose save
// threw must not appear on screen as though it were in the book.
function farmersThatSaved(farmersIn, saveResults) {
  const okAt = new Set((saveResults || []).map((r, i) => (r && r.ok ? i : -1)).filter((i) => i >= 0));
  return (farmersIn || []).filter((_, i) => okAt.has(i));
}

// ── a 578 that arrived as a DOCUMENT ────────────────────────────────────────
// Ordered exactly as the Box sync orders its gates, and returning the farmer_578s row
// to write only when every gate passes. The row lands NOT forwarded; api/box.js
// ingestStored578 then sends the one email for a live-campaign 578 and marks it forwarded
// only on an accepted send (fsa578Intake emailOnArrival + forwardedPatch, 2026-10-19).
function stored578Decision({ sameContent, extractError, extracted, farmerIdHint, aiMatch, dupFound, isRevision }) {
  if (sameContent) return { decision: "duplicate_content", existingId: sameContent.id || null };
  if (extractError) return { decision: "extract_failed", error: String(extractError).slice(0, 300) };
  if (!extracted || extracted.notFsa578) return { decision: "not_578" };
  const year = Number.isInteger(extracted.cropYear) ? extracted.cropYear : null;
  if (!year) return { decision: "year_unknown" };
  const hint = farmerIdHint && UUID.test(String(farmerIdHint)) ? farmerIdHint : null;
  const ai = aiMatch && aiMatch.confidence >= 80 && aiMatch.farmer && UUID.test(String(aiMatch.farmer.id)) ? aiMatch.farmer.id : null;
  // The policy number in the file name is the AIP's own key; it outranks a name match.
  // Where the two NAME DIFFERENT farms nothing is claimed — the row lands unmatched.
  const farmerId = hint && ai && hint !== ai ? null : hint || ai;
  const matchedBy = !farmerId ? null : hint ? "policy_number" : "ai";
  if (dupFound && !isRevision) return { decision: "duplicate", farmerId, year };
  return { decision: "import", farmerId, matchedBy, year, conflict: !!(hint && ai && hint !== ai) };
}

function stored578Row({ decision, extracted, season, source, storagePath, fileName, sha256, isRevision }) {
  return {
    farmer_id: decision.farmerId || null,
    county: (extracted && extracted.county) || null,
    tax_year: decision.year,
    season,
    is_nationwide: !!(extracted && extracted.isNationwide),
    source,
    storage_path: storagePath,
    ...INTAKE.initialForwardState({ matched: !!decision.farmerId }),
    // Queued for its one read (sql/104, src/fsa578Lines.js).
    ...INTAKE.arrivalLinesState(),
    matched_by: decision.matchedBy || null,
    extracted_name: (extracted && extracted.producerName) || null,
    original_filename: fileName || null,
    content_sha256: INTAKE.isSha256Hex(sha256) ? sha256 : null,
    notes: isRevision ? "REVISION — filed from a stored document" : null,
  };
}

// What farmer_documents.parse_status says about a document handed to the 578 intake.
const PARSE_STATUS_FOR_578 = {
  import: "filed_578",
  duplicate: "duplicate_578",
  duplicate_content: "duplicate_578",
  not_578: "not_578",
  year_unknown: "578_year_unknown",
  extract_failed: "578_extract_failed",
  insert_failed: "578_insert_failed",
};
function parseStatusFor578(decision) {
  return PARSE_STATUS_FOR_578[decision] || null;
}

module.exports = {
  DOC_TYPE_FOR_EXTRACTION,
  docTypeForExtraction,
  documentStoragePath,
  sameDocumentFilter,
  samePathFilter,
  isMissingIndexColumn,
  withoutIndexColumns,
  indexRow,
  originalsFromResults,
  savedFarmerIdFor,
  farmersThatSaved,
  stored578Decision,
  stored578Row,
  parseStatusFor578,
};
